'use client';

import { useSyncExternalStore } from 'react';

interface NativeState {
  isNative: boolean;
  isReady: boolean;
}

interface CapacitorGlobal {
  isNativePlatform: () => boolean;
}

// Matches Tailwind's `md` breakpoint — below this width the editorial native
// chrome should take over so mobile Safari / PWA users get the same look as
// Capacitor users. Only the shell-level hook (`useNativeShell`) consumes this;
// `useNativeDetect` remains Capacitor-only so biometric forms never fire on
// plain mobile browsers.
const MOBILE_BREAKPOINT_QUERY = '(max-width: 767px)';

function getCapacitorGlobal(): CapacitorGlobal | undefined {
  if (typeof window === 'undefined') return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cap = (window as any).Capacitor as CapacitorGlobal | undefined;
  return cap && typeof cap.isNativePlatform === 'function' ? cap : undefined;
}

function isCapacitorRuntime(): boolean {
  return getCapacitorGlobal()?.isNativePlatform() ?? false;
}

function isMobileViewport(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
}

const NATIVE_STATE: NativeState = { isNative: true, isReady: true };
const WEB_STATE: NativeState = { isNative: false, isReady: true };
const SERVER_STATE: NativeState = { isNative: false, isReady: false };

function getCapacitorSnapshot(): NativeState {
  return isCapacitorRuntime() ? NATIVE_STATE : WEB_STATE;
}

function getShellSnapshot(): NativeState {
  return isCapacitorRuntime() || isMobileViewport() ? NATIVE_STATE : WEB_STATE;
}

function getServerSnapshot(): NativeState {
  return SERVER_STATE;
}

function subscribeNoop() {
  return () => {};
}

function subscribeViewport(onChange: () => void) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const mql = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

/**
 * Returns `isNative: true` only when running inside a Capacitor wrapper.
 *
 * Use this for anything that depends on actual native APIs — biometric
 * authentication, Face ID, the Capacitor-only sign-in/sign-up forms.
 * Do NOT use this to decide page chrome; mobile Safari users will get
 * `false` here and would be left with Capacitor-only UI that they cannot
 * interact with.
 */
export function useNativeDetect(): NativeState {
  return useSyncExternalStore(
    subscribeNoop,
    getCapacitorSnapshot,
    getServerSnapshot,
  );
}

/**
 * Returns `isNative: true` when the user should see the editorial native
 * shell — either inside Capacitor, OR on a mobile-width viewport in any
 * browser. Use this for layout/chrome decisions so mobile Safari / PWA
 * users get the same look as Capacitor users.
 *
 * Subscribes to viewport changes so resizing or rotating across the
 * breakpoint hot-swaps the shell without a refresh.
 */
export function useNativeShell(): NativeState {
  return useSyncExternalStore(
    subscribeViewport,
    getShellSnapshot,
    getServerSnapshot,
  );
}
